import { AxiosResponse } from "axios"
import { addDays, format, parseISO } from "date-fns"
import { apiInstance } from "../base"
import {
    ILoyaltyAccrualsQuery,
    ILoyaltyAccrualsResponse,
    ILoyaltyAnalyticsCommonQuery,
    ILoyaltyAnalyticsPeriodQuery,
    ILoyaltyLevelBreakdownResponse,
    ILoyaltyStoresResponse,
    ILoyaltySummaryResponse,
    ILoyaltyTimeSeriesQuery,
    ILoyaltyTimeSeriesResponse,
} from "./types"

const BASE_PATH = "/loyalty-levels/analytics"

const appendArray = (
    params: Record<string, string | number | boolean>,
    key: string,
    values?: Array<string | number>
) => {
    if (values?.length) {
        params[key] = values.join(",")
    }
}

// PeriodPicker emits inclusive yyyy-MM-dd dates; analytics endpoints use the half-open [from, to) interval.
const toExclusiveEndDay = (dateStr: string): string => {
    const parsed = parseISO(dateStr)
    if (Number.isNaN(parsed.getTime())) return dateStr

    return format(addDays(parsed, 1), "yyyy-MM-dd")
}

const buildPeriodParams = (
    query: ILoyaltyAnalyticsPeriodQuery
): Record<string, string | number | boolean> => ({
    from: query.from,
    to: toExclusiveEndDay(query.to),
})

const buildCommonParams = (
    query: ILoyaltyAnalyticsCommonQuery
): Record<string, string | number | boolean> => {
    const params = buildPeriodParams(query)

    appendArray(params, "storeIds", query.storeIds)
    appendArray(params, "levels", query.levels)
    if (query.includeUnassignedStore) params.includeUnassignedStore = true
    if (query.includeUnknownLevel) params.includeUnknownLevel = true
    if (query.includeReturns) params.includeReturns = true

    return params
}

export const getLoyaltyAccruals = (
    query: ILoyaltyAccrualsQuery
): Promise<AxiosResponse<ILoyaltyAccrualsResponse>> => {
    const params = buildCommonParams(query)
    if (query.page) params.page = query.page
    if (query.pageSize) params.pageSize = query.pageSize

    return apiInstance.handler.get<ILoyaltyAccrualsResponse>(`${BASE_PATH}/accruals`, { params })
}

export const getLoyaltySummary = (
    query: ILoyaltyAnalyticsCommonQuery
): Promise<AxiosResponse<ILoyaltySummaryResponse>> => {
    return apiInstance.handler.get<ILoyaltySummaryResponse>(`${BASE_PATH}/summary`, {
        params: buildCommonParams(query),
    })
}

export const getLoyaltyTimeSeries = (
    query: ILoyaltyTimeSeriesQuery
): Promise<AxiosResponse<ILoyaltyTimeSeriesResponse>> => {
    const params = buildCommonParams(query)
    params.interval = query.interval

    return apiInstance.handler.get<ILoyaltyTimeSeriesResponse>(`${BASE_PATH}/time-series`, { params })
}

export const getLoyaltyLevelBreakdown = (
    query: ILoyaltyAnalyticsPeriodQuery
): Promise<AxiosResponse<ILoyaltyLevelBreakdownResponse>> => {
    return apiInstance.handler.get<ILoyaltyLevelBreakdownResponse>(`${BASE_PATH}/level-breakdown`, {
        params: buildPeriodParams(query),
    })
}

export const getLoyaltyStores = (
    query: ILoyaltyAnalyticsPeriodQuery
): Promise<AxiosResponse<ILoyaltyStoresResponse>> => {
    return apiInstance.handler.get<ILoyaltyStoresResponse>(`${BASE_PATH}/stores`, {
        params: buildPeriodParams(query),
    })
}

export const exportLoyaltyAccruals = (
    query: ILoyaltyAnalyticsCommonQuery
): Promise<AxiosResponse<Blob>> => {
    return apiInstance.handler.get<Blob>(`${BASE_PATH}/export`, {
        params: buildCommonParams(query),
        responseType: "blob",
    })
}
